import { Grid } from '@material-ui/core'
import type { StyleClasses, DialogProps } from '@paintscout/ui'
import type { Theme } from '@material-ui/core/styles'
import { createStyles, withStyles } from '@material-ui/core/styles'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputField,
  ActiveSwitch
} from '@paintscout/ui'
import type { AreaLabelOption } from '@paintscout/util/builder'
import { getObjectLabels, updateAreaLabelOption } from '@paintscout/util/builder'
import clone from 'lodash/clone'
import type { OptionsDocument } from 'paintscout'
import React from 'react'
import CloseIcon from '@material-ui/icons/Close'
import QuoteTypeSelect from '../../QuoteTypeSelect'
import { useFlags } from 'shared/components/common/FlagsProvider'

export interface EditAreaOptionDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof styles>
  item: AreaLabelOption
  options: OptionsDocument
  isNew?: boolean
  onConfirm?: (options: OptionsDocument, isDirty?: boolean) => void
  onCancel?: () => void
  showActiveToggle?: boolean
}

export interface EditAreaOptionDialogState {
  item: AreaLabelOption
  isDirty?: boolean
}

const styles = (theme: Theme) => {
  return createStyles({
    root: {},
    input: {},
    divider: {
      marginBottom: theme.spacing(4)
    },
    overflowVisible: {
      overflow: 'visible'
    },
    dialogTitle: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    switchLabel: {
      marginLeft: '0 !important',
      marginRight: 0
    },
    dialogContent: {
      maxHeight: '70vh',
      overflowY: 'auto',
      [theme.breakpoints.down('sm')]: {
        maxHeight: '75vh'
      }
    }
  })
}

class EditAreaOptionDialog extends React.PureComponent<EditAreaOptionDialogProps, EditAreaOptionDialogState> {
  private signatureData?: string

  constructor(props: EditAreaOptionDialogProps) {
    super(props)

    const { item } = this.props

    this.state = {
      item: clone(item),
      isDirty: false
    }
  }

  public render() {
    const { loading, classes, options, onCancel, showActiveToggle, ...otherProps } = this.props
    const { item } = this.state
    const objectLabels = getObjectLabels({ options })

    return (
      <Dialog
        classes={{ root: classes.root, paper: classes.overflowVisible }}
        fullWidth={true}
        maxWidth={'md'}
        {...otherProps}
      >
        <DialogTitle
          classes={{ root: classes.dialogTitle }}
          rightContent={
            <IconButton size="small" onClick={onCancel} disabled={loading}>
              <CloseIcon />
            </IconButton>
          }
        >
          {`${this.props.isNew ? 'Add' : 'Edit'} Area`}
        </DialogTitle>

        <DialogContent classes={{ root: classes.dialogContent }} className={classes.overflowVisible}>
          <form
            id="area-option"
            onSubmit={(ev) => {
              ev.preventDefault()
              this.handleConfirm()
            }}
          >
            <Grid container={true} spacing={3}>
              <Grid item={true} xs={12} sm={12} md={6}>
                <InputField
                  classes={{ root: classes.input }}
                  label={'Label or Name'}
                  name={'label'}
                  value={item.label}
                  fullWidth={true}
                  onChange={this.handleChange}
                  required={true}
                />
              </Grid>
              <Grid item={true} xs={12} sm={12} md={6}>
                <QuoteTypeSelect
                  label={`${objectLabels.quote.value} Types`}
                  variant="multi"
                  disablePortal={false}
                  hideCloseButton
                  onChange={this.handleQuoteTypeChange}
                  value={item.quoteTypes}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions
          showBorder={true}
          leftButton={
            showActiveToggle ? (
              <FormControlLabel
                className={classes.switchLabel}
                control={
                  <ActiveSwitch
                    color="primary"
                    checked={typeof item.active !== 'undefined' ? !!item.active : true}
                    onChange={this.handleToggle}
                  />
                }
                label="Active"
                labelPlacement="end"
              />
            ) : undefined
          }
        >
          <Button type="submit" form="area-option" disabled={loading} variant={'contained'}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  public handleChange = (event: any) => {
    const { name, value } = event.target

    this.setState({
      item: {
        ...this.state.item,
        [name]: value
      },
      isDirty: true
    })
  }

  public handleCheckboxChange = (event: any, value: string) => {
    const { name } = event.target

    this.setState({
      item: {
        ...this.state.item,
        [name]: value
      },
      isDirty: true
    })
  }

  public handleConfirm = () => {
    if (this.props.onConfirm) {
      const { options } = this.props
      const updatedOptions = updateAreaLabelOption({ options, areaLabelOption: this.state.item })
      this.props.onConfirm(updatedOptions, this.state.isDirty)
    }
  }

  public handleQuoteTypeChange = (values: string[]) => {
    this.setState({
      item: {
        ...this.state.item,
        quoteTypes: values
      },
      isDirty: true
    })
  }

  public handleToggle = () => {
    this.setState({
      item: {
        ...this.state.item,
        active: !this.state.item.active
      },
      isDirty: true
    })
  }
}

const StyledEditAreaOptionDialog = withStyles(styles)(EditAreaOptionDialog)

function EditAreaOptionDialogWithFlags(props: Omit<EditAreaOptionDialogProps, 'showActiveToggle'>) {
  const { canDeactivateSettingsItems } = useFlags()
  return <StyledEditAreaOptionDialog {...props} showActiveToggle={!!canDeactivateSettingsItems} />
}

export default EditAreaOptionDialogWithFlags
